import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building, Eye, Users, Receipt, CheckCircle, Handshake } from 'lucide-react';
import axios from 'axios';
import { OFFICIAL_PARTNERS } from '../data/partners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnersPage = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_name: '',
    email: '',
    phone: '',
    partnership_type: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, partnership_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/partner`, formData);
      toast.success(t.partner.successMsg);
      setFormData({
        organization_name: '',
        contact_name: '',
        email: '',
        phone: '',
        partnership_type: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting partner form:', error);
      toast.error(t.common.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { id: 'visibility', icon: Eye, text: t.partner.benefit1 },
    { id: 'network', icon: Handshake, text: t.partner.benefit2 },
    { id: 'community', icon: Users, text: t.partner.benefit3 },
    { id: 'tax', icon: Receipt, text: t.partner.benefit4 },
  ];

  const partnershipTypes = [
    { value: 'financial', label: t.partner.types.financial },
    { value: 'service', label: t.partner.types.service },
    { value: 'event', label: t.partner.types.event },
    { value: 'other', label: t.partner.types.other },
  ];

  const officialPartners = OFFICIAL_PARTNERS;

  return (
    <div data-testid="partners-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">{t.nav.partners}</span>
            <h1 
              data-testid="partners-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mt-4 mb-6"
            >
              {t.partner.pageTitle}
            </h1>
            <p className="text-lg text-[#546A76] leading-relaxed">
              {t.partner.whyText}
            </p>
          </div>
        </div>
      </section>

      {/* Official Partners */}
      <section className="py-20 bg-white border-b border-[#E2E8F0]" data-testid="official-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-3">
            {language === 'fr' ? 'Nos partenaires officiels' : 'Our Official Partners'}
          </h2>
          <p className="text-[#546A76] mb-12 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Merci à nos partenaires qui soutiennent notre mission auprès des femmes et enfants victimes de violence.'
              : 'Thank you to our partners who support our mission for women and children victims of violence.'}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {officialPartners.map((partner) => (
              <a
                key={partner.name}
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`partner-card-${partner.id}`}
                className="bg-white rounded-3xl border border-[#E2E8F0] shadow-sm p-8 w-64 flex flex-col items-center hover:shadow-md hover:border-[#D4AF37]/50 transition-all"
              >
                <img
                  src={partner.logo}
                  alt={`Logo ${partner.name}`}
                  loading="lazy"
                  className="w-36 h-36 object-contain mb-4"
                />
                <p className="font-serif font-semibold text-[#1A252C]">{partner.name}</p>
                <p className="text-xs text-[#D4AF37] font-medium tracking-widest uppercase mt-1">
                  {language === 'fr' ? 'Partenaire officiel' : 'Official Partner'}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Benefits */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-8">
                {t.partner.whyTitle}
              </h2>
              <p className="text-[#546A76] leading-relaxed mb-10">
                {t.partner.whyText}
              </p>

              <h3 className="font-serif text-xl font-semibold text-[#1A252C] mb-6">
                {t.partner.benefitsTitle}
              </h3>
              <div className="space-y-4">
                {benefits.map((item) => (
                  <div 
                    key={item.id}
                    data-testid={`benefit-item-${item.id}`}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-[#FAFAFA]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <p className="text-[#1A252C] pt-2">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Stats Box */}
              <div className="mt-12 bg-[#4A708B] rounded-3xl p-8 text-center">
                <Building className="w-12 h-12 text-white/80 mx-auto mb-4" />
                <p className="text-4xl font-bold text-white mb-2">15+</p>
                <p className="text-white/80">
                  {t.partner.benefit2}
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              <div className="bg-[#FAFAFA] rounded-3xl p-8 sm:p-10">
                <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
                  {t.partner.formTitle}
                </h2>

                <form onSubmit={handleSubmit} data-testid="partner-form" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="organization_name">{t.partner.orgName} *</Label>
                    <Input
                      id="organization_name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleChange}
                      required
                      data-testid="partner-orgname"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_name">{t.partner.contactName} *</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      required
                      data-testid="partner-contactname"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t.partner.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="partner-email"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t.partner.phone} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="partner-phone"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="partnership_type">{t.partner.partnershipType} *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.partnership_type}>
                      <SelectTrigger 
                        data-testid="partner-type"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      >
                        <SelectValue placeholder={t.partner.partnershipType} />
                      </SelectTrigger>
                      <SelectContent>
                        {partnershipTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t.partner.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="partner-message"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="partner-submit"
                    className="w-full gold-btn rounded-full py-6 text-lg font-medium"
                  >
                    {isSubmitting ? t.common.loading : t.partner.submit}
                    {!isSubmitting && <CheckCircle className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
